import { SHIP } from "@tiny-seas/shared";

/**
 * Hammer-and-plank repair timing, keyed to the replicated `actionProgress`
 * (0 → 1 over `SHIP.repairSeconds`). Shared by every character rig and the
 * nail-strike audio so hits land in sync. Babylon-free and allocation-free.
 */
export const REPAIR_SWING = {
  /** Progress spent bringing the plank up to the hole and the hammer to ready. */
  setup: 0.16,
  strikes: 4,
  /** Progress per strike (windup → hit → rebound). */
  span: 0.21,
  /** Share of a strike before the hammer reaches the plank. */
  hit: 0.72,
  /** Share of a strike spent winding up; the fall to the hit follows. */
  windup: 0.58,
  /** Hammer raise between strikes, as a share of the full windup. */
  ready: 0.3,
} as const;

const smooth = (t: number): number => { const v = Math.max(0, Math.min(1, t)); return v * v * (3 - 2 * v); };

/** Progress at which strike `index` (0-based) lands on the plank. */
export function repairStrikeAt(index: number): number {
  return REPAIR_SWING.setup + REPAIR_SWING.span * (index + REPAIR_SWING.hit);
}

/** Number of strikes that landed in the progress interval (from, to]. */
export function repairStrikesBetween(from: number, to: number): number {
  let n = 0;
  for (let i = 0; i < REPAIR_SWING.strikes; i++) {
    const at = repairStrikeAt(i);
    if (at > from && at <= to) n++;
  }
  return n;
}

export interface RepairSwingSample {
  /** 0 = hammer face on the plank, 1 = full windup. */
  raise: number;
  /** 0 … 1 approach of the plank and hammer from below the work. */
  setup: number;
}

/** Strike cycle: slow windup, fast accelerating fall onto the nail, short rebound to ready. */
export function sampleRepairSwing(progress: number, out: RepairSwingSample): RepairSwingSample {
  const S = REPAIR_SWING;
  const p = Math.max(0, Math.min(1, progress));
  out.setup = smooth(p / S.setup);
  const strike = (p - S.setup) / S.span;
  if (strike < 0) { out.raise = S.ready * out.setup; return out; }
  if (strike >= S.strikes) { out.raise = S.ready; return out; }
  const u = strike - Math.floor(strike);
  if (u < S.windup) out.raise = S.ready + (1 - S.ready) * smooth(u / S.windup);
  else if (u < S.hit) { const t = (u - S.windup) / (S.hit - S.windup); out.raise = 1 - t * t; }
  else { const t = (u - S.hit) / (1 - S.hit); out.raise = S.ready * (1 - (1 - t) * (1 - t)); }
  return out;
}

/** Furthest the clock may run ahead of the last snapshot: a few patch intervals of progress. */
const CLOCK_LEAD = 0.05;

/**
 * Continuous, monotonic estimate of the server's repair progress. Snapshots
 * arrive at the patch rate; advancing at the authoritative rate between them
 * keeps the fast hammer fall smooth. It catches up when behind, never runs
 * back (which would replay a strike), and stalls just ahead of a stalled server.
 */
export class RepairClock {
  private active = false;
  value = 0;

  advance(repairing: boolean, progress: number, dt: number): number {
    if (!repairing) { this.active = false; return this.value = 0; }
    if (!this.active || Math.abs(progress - this.value) > 0.2) this.value = progress;
    else {
      const step = dt / SHIP.repairSeconds;
      const next = this.value + step + Math.max(0, progress - this.value - step) * Math.min(1, dt * 4);
      this.value = Math.min(1, progress + CLOCK_LEAD, Math.max(this.value, next));
    }
    this.active = true;
    return this.value;
  }
}
